import { useEffect, useState, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ShoppingCart,
  DollarSign,
  Calendar,
  TrendingUp,
  BarChart3,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useToast } from "../components/Toast";
import { httpGet } from "../http";
import { MoneyField } from "../helpers/br_number_field";
import { NavigateFunction, useNavigate } from "react-router-dom";

const getUTCDateString = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth()).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const createUTCDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(Date.UTC(year, month, day));
};

const newUTCDate = () => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
};

type StatisticsType = {
  count: number;
  sum: number;
  dailyAverage: number;
  weeklyAverage: number;
  series: Array<{
    date: string;
    count: number;
    sum: number;
  }>;
};

type StateType = {
  statisticsLoaded: boolean;
  statistics: StatisticsType | null;
};

const loadStatistics = async (
  startDate: string,
  endDate: string,
  setStatistics: Function,
  navigate: NavigateFunction,
  showError: (message: string) => void,
) => {
  try {
    const result = await httpGet(
      `/api/v1/purchases/statistics?startDate=${startDate}&endDate=${endDate}`,
    );

    if (result.error === "logged_out") {
      navigate("/dash/logout");
    } else if (result.error === "offline") {
      setStatistics(result.body);
    } else if (result.error) {
      showError("Falha ao obter estatísticas");
    } else if (result.body) {
      setStatistics(result.body);
    }
  } finally {
  }
};

export const Dash = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [state, setState] = useState<StateType>({
    statisticsLoaded: false,
    statistics: null,
  });

  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const [dateRange, setDateRange] = useState(() => {
    const endDate = newUTCDate();
    const startDate = newUTCDate();
    startDate.setMonth(startDate.getMonth() - 3);
    return {
      startDate: getUTCDateString(startDate),
      endDate: getUTCDateString(endDate),
    };
  });

  const [errors, setErrors] = useState<{
    startDate?: string;
    endDate?: string;
  }>({});

  const validateDateRange = (start: string, end: string) => {
    const newErrors: { startDate?: string; endDate?: string } = {};
    const startDate = createUTCDate(start);
    const endDate = createUTCDate(end);
    const today = newUTCDate();
    const sixMonthsAgo = newUTCDate();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    sixMonthsAgo.setHours(0, 0, 0, 0);

    if (endDate.getTime() > today.getTime()) {
      newErrors.endDate = "Data final não pode ser no futuro";
    }

    if (startDate.getTime() < sixMonthsAgo.getTime()) {
      newErrors.startDate = "Data inicial não pode ser mais de 6 meses atrás";
    }

    if (startDate.getTime() > endDate.getTime()) {
      newErrors.startDate = "Data inicial deve ser anterior à data final";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchStatistics = useCallback(
    (startDate: string, endDate: string) => {
      if (!validateDateRange(startDate, endDate)) {
        return;
      }

      setState((prev) => ({ ...prev, statisticsLoaded: false }));

      const setStatistics = (statistics: StatisticsType | null) => {
        setState((prev) => ({
          ...prev,
          statisticsLoaded: true,
          statistics: statistics,
        }));
      };

      loadStatistics(startDate, endDate, setStatistics, navigate, showError);
    },
    [navigate, showError],
  );

  useEffect(() => {
    if (!state.statisticsLoaded) {
      fetchStatistics(dateRange.startDate, dateRange.endDate);
    }
  }, [
    state.statisticsLoaded,
    dateRange.startDate,
    dateRange.endDate,
    fetchStatistics,
  ]);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Visão geral dos seus gastos</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
        <button
          onClick={() => navigate("/dash/purchases/new")}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow text-left group"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
            Nova Compra
          </h3>
          <p className="text-gray-600 text-sm">
            Registre uma nova compra escaneando o cupom fiscal
          </p>
        </button>

        <button
          onClick={() => navigate("/dash/shopping-lists")}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow text-left group"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <svg
                className="h-6 w-6 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
            Listas de Compras
          </h3>
          <p className="text-gray-600 text-sm">
            Gerencie suas listas de compras e organize seus produtos
          </p>
        </button>

        <button
          onClick={() => navigate("/dash/purchases")}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow text-left group"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors">
            Histórico de Compras
          </h3>
          <p className="text-gray-600 text-sm">
            Veja o histórico completo de todas as suas compras
          </p>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
        >
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-gray-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Período de Consulta
            </h3>
          </div>
          <div className="flex items-center">
            {Object.keys(errors).length === 0 &&
              dateRange.startDate &&
              dateRange.endDate && (
                <div className="text-sm text-gray-600 mr-3">
                  {createUTCDate(dateRange.startDate).toLocaleDateString(
                    "pt-BR",
                  )}{" "}
                  até{" "}
                  {createUTCDate(dateRange.endDate).toLocaleDateString("pt-BR")}
                </div>
              )}
            {isFiltersExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </div>
        </div>

        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isFiltersExpanded
              ? "max-h-96 opacity-100 mt-4"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { label: "Último mês", months: 1 },
              { label: "Últimos 3 meses", months: 3 },
              { label: "Últimos 6 meses", months: 6 },
            ].map(({ label, months }) => (
              <button
                key={months}
                onClick={() => {
                  const endDate = newUTCDate();
                  const startDate = newUTCDate();
                  startDate.setMonth(startDate.getMonth() - months);
                  const newRange = {
                    startDate: getUTCDateString(startDate),
                    endDate: getUTCDateString(endDate),
                  };
                  setDateRange(newRange);
                  setState((prev) => ({ ...prev, statisticsLoaded: false }));
                }}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-3 items-end">
            <div>
              <label
                htmlFor="startDate"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Data Inicial
              </label>
              <input
                type="date"
                id="startDate"
                value={dateRange.startDate}
                onChange={(e) => {
                  const newStartDate = e.target.value;
                  setDateRange((prev) => ({
                    ...prev,
                    startDate: newStartDate,
                  }));
                  setState((prev) => ({ ...prev, statisticsLoaded: false }));
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.startDate ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="endDate"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Data Final
              </label>
              <input
                type="date"
                id="endDate"
                value={dateRange.endDate}
                onChange={(e) => {
                  const newEndDate = e.target.value;
                  setDateRange((prev) => ({ ...prev, endDate: newEndDate }));
                  setState((prev) => ({ ...prev, statisticsLoaded: false }));
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.endDate ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.endDate && (
                <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
              )}
            </div>
            <div>
              <button
                onClick={() =>
                  fetchStatistics(dateRange.startDate, dateRange.endDate)
                }
                disabled={Object.keys(errors).length > 0}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Atualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8 relative">
        {!state.statisticsLoaded && (
          <div className="absolute inset-0 bg-white bg-opacity-80 rounded-xl flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Carregando dados...</p>
            </div>
          </div>
        )}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">
                {state.statistics?.count ?? 0}
              </p>
              <p className="text-sm text-gray-600">compras</p>
            </div>
          </div>
          <div className="flex items-center">
            <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
            <span className="text-sm text-gray-600">Período selecionado</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">
                <MoneyField value={state.statistics?.sum ?? 0} />
              </p>
              <p className="text-sm text-gray-600">total gasto</p>
            </div>
          </div>
          <div className="flex items-center">
            <BarChart3 className="h-4 w-4 text-green-500 mr-1" />
            <span className="text-sm text-gray-600">No período</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">
                <MoneyField value={state.statistics?.dailyAverage ?? 0} />
              </p>
              <p className="text-sm text-gray-600">por dia</p>
            </div>
          </div>
          <div className="flex items-center">
            <TrendingUp className="h-4 w-4 text-purple-500 mr-1" />
            <span className="text-sm text-gray-600">Média diária</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-orange-600" />
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">
                <MoneyField value={state.statistics?.weeklyAverage ?? 0} />
              </p>
              <p className="text-sm text-gray-600">por semana</p>
            </div>
          </div>
          <div className="flex items-center">
            <TrendingUp className="h-4 w-4 text-orange-500 mr-1" />
            <span className="text-sm text-gray-600">Média semanal</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden relative">
        {!state.statisticsLoaded && (
          <div className="absolute inset-0 bg-white bg-opacity-80 rounded-xl flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Carregando dados...</p>
            </div>
          </div>
        )}
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex items-center">
            <TrendingUp className="h-5 w-5 text-gray-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Evolução dos Gastos
            </h3>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Acompanhe o crescimento acumulado dos seus gastos ao longo do tempo
          </p>
        </div>
        <div className="p-6 pb-24">
          {state.statistics?.series ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={state.statistics.series.map((item, index) => {
                  const cumulativeSum = state
                    .statistics!.series.slice(0, index + 1)
                    .reduce((acc, curr) => acc + curr.sum, 0);
                  const cumulativeCount = state
                    .statistics!.series.slice(0, index + 1)
                    .reduce((acc, curr) => acc + curr.count, 0);

                  return {
                    date: new Date(item.date).toLocaleDateString("pt-BR"),
                    cumulativeSum,
                    cumulativeCount,
                    originalSum: item.sum,
                    originalCount: item.count,
                  };
                })}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  className="text-gray-600"
                />
                <YAxis
                  yAxisId="sum"
                  orientation="left"
                  className="text-gray-600"
                />
                <Tooltip
                  formatter={(value, name, props) => {
                    if (props.dataKey === "cumulativeSum") {
                      return [`R$ ${value}`, "Gasto Acumulado"];
                    }
                    return [value, name];
                  }}
                  labelFormatter={(label, payload) => {
                    return `Data: ${label}`;
                  }}
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                />
                <Legend />
                <Line
                  yAxisId="sum"
                  type="monotone"
                  dataKey="cumulativeSum"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: "#3b82f6", strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, stroke: "#3b82f6", strokeWidth: 2 }}
                  name="Gasto Acumulado"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Carregando dados...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
